import { Injectable } from '@angular/core';
import {
    CleanedDomainData,
    DomainValidationResult,
    ValidationSummary,
} from '../types/domain-management.types';

@Injectable({
    providedIn: 'root',
})
export class DomainValidatorService {

    /**
     * Validate domain data for creation with enhanced error reporting
     */
    validateDomainForCreation (domainData: CleanedDomainData): DomainValidationResult {
        const errors: string[] = [];
        const warnings: string[] = [];
        const suggestions: string[] = [];
        const fieldErrors: { [field: string]: string[] } = {};

        // Required field validation
        if (!domainData.url) {
            const error = 'Domain URL is required';
            errors.push(error);
            fieldErrors.url = [error];
            suggestions.push('Please provide a valid domain URL (e.g., https://example.com)');
        }

        if (!domainData.title) {
            const error = 'Domain title is required';
            errors.push(error);
            fieldErrors.title = [error];
            suggestions.push('Please provide a descriptive title for the domain');
        }

        // URL format validation
        if (domainData.url) {
            try {
                const url = new URL(domainData.url);

                // Check for common URL issues
                if (url.protocol !== 'http:' && url.protocol !== 'https:') {
                    const error = `Unsupported protocol '${url.protocol}'. Only HTTP and HTTPS are supported`;
                    errors.push(error);
                    fieldErrors.url = fieldErrors.url || [];
                    fieldErrors.url.push(error);
                }

                if (url.hostname.includes('localhost') || url.hostname.includes('127.0.0.1')) {
                    warnings.push('Local development URLs may not be accessible for scanning');
                }

                if (url.pathname !== '/' && url.pathname !== '') {
                    warnings.push('URL contains a path. Consider using the root domain for better scanning coverage');
                }

            } catch (urlError) {
                const error = `Invalid URL format: ${domainData.url}. Please provide a valid URL (e.g., https://example.com)`;
                errors.push(error);
                fieldErrors.url = fieldErrors.url || [];
                fieldErrors.url.push(error);
                suggestions.push('Ensure the URL includes the protocol (http:// or https://) and a valid domain name');
            }
        }

        // Additional domain data validation
        if (domainData.path_constraints) {
            const constraintErrors = this.validatePathConstraints(domainData.path_constraints);
            errors.push(...constraintErrors);
        }

        if (domainData.link_excludes) {
            const excludeErrors = this.validateLinkExcludes(domainData.link_excludes);
            errors.push(...excludeErrors);
        }

        if (domainData.domain_groups) {
            const groupErrors = this.validateDomainGroups(domainData.domain_groups);
            errors.push(...groupErrors);
        }

        // Basic domain structure validation completed
        return {
            isValid: errors.length === 0,
            url: domainData.url || '',
            title: domainData.title,
            errors: errors,
            warnings: warnings,
            suggestions: suggestions.length > 0 ? suggestions : undefined,
            fieldErrors: Object.keys(fieldErrors).length > 0 ? fieldErrors : undefined,
        };
    }

    /**
     * Generate validation summary for multiple domains
     */
    generateValidationSummary (domains: CleanedDomainData[]): ValidationSummary {
        let validDomains = 0;
        let invalidDomains = 0;
        let warningCount = 0;
        const criticalErrors: string[] = [];

        domains.forEach((domain, index) => {
            const validation = this.validateDomainForCreation(domain);
            if (validation.isValid) {
                validDomains++;
            } else {
                invalidDomains++;
                // Collect critical errors that would prevent import
                validation.errors.forEach(error => {
                    if (error.includes('required') || error.includes('Invalid URL')) {
                        criticalErrors.push(`Domain ${index + 1}: ${error}`);
                    }
                });
            }
            warningCount += validation.warnings.length;
        });

        return {
            totalDomains: domains.length,
            validDomains,
            invalidDomains,
            duplicates: 0, // Could add duplicate detection logic here
            errors: criticalErrors,
            warningCount,
            criticalErrors: criticalErrors.slice(0, 10), // Limit to first 10 critical errors
        };
    }

    /**
     * Validate URL format
     */
    validateUrl (url: string): boolean {
        try {
            new URL(url);
            return true;
        } catch {
            return false;
        }
    }

    /**
     * Validate required domain fields with enhanced error messages
     */
    validateRequiredFields (domain: Record<string, unknown>): string[] {
        const errors: string[] = [];

        if (!domain.url || typeof domain.url !== 'string' || domain.url.trim() === '') {
            errors.push('URL is required and must be a non-empty string');
        }

        if (!domain.title || typeof domain.title !== 'string' || domain.title.trim() === '') {
            errors.push('Title is required and must be a non-empty string');
        } else if (domain.title.length > 255) {
            errors.push('Title must be 255 characters or less');
        }

        // Validate URL format if provided
        if (domain.url && typeof domain.url === 'string' && domain.url.trim() !== '') {
            if (!this.validateUrl(domain.url)) {
                errors.push(`Invalid URL format: ${domain.url}. Must be a valid HTTP or HTTPS URL`);
            }
        }

        return errors;
    }

    /**
     * Validate path constraints array
     */
    private validatePathConstraints (constraints: unknown[]): string[] {
        const errors: string[] = [];

        if (!Array.isArray(constraints)) {
            errors.push('path_constraints must be an array');
            return errors;
        }

        constraints.forEach((constraint, index) => {
            if (!constraint || typeof constraint !== 'object') {
                errors.push(`path_constraints[${index}] must be an object`);
            }
        });

        return errors;
    }

    /**
     * Validate link excludes array
     */
    private validateLinkExcludes (excludes: unknown[]): string[] {
        const errors: string[] = [];

        if (!Array.isArray(excludes)) {
            errors.push('link_excludes must be an array');
            return errors;
        }

        excludes.forEach((exclude, index) => {
            if (!exclude || typeof exclude !== 'object') {
                errors.push(`link_excludes[${index}] must be an object`);
            }
        });

        return errors;
    }

    /**
     * Validate domain groups array
     */
    private validateDomainGroups (groups: unknown[]): string[] {
        const errors: string[] = [];

        if (!Array.isArray(groups)) {
            errors.push('domain_groups must be an array');
            return errors;
        }

        groups.forEach((group, index) => {
            if (!group || typeof group !== 'object') {
                errors.push(`domain_groups[${index}] must be an object`);
            }
        });

        return errors;
    }
}
